import React, { useState, forwardRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  IconButton,
  FormControl,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import { IoMdArrowDropdown } from "react-icons/io";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import axios from "axios";

const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
  <div
    className="custom-date-input"
    onClick={onClick}
    ref={ref}
    style={{
      display: "flex",
      alignItems: "center",
      border: "1px solid #ccc",
      borderRadius: "20px",
      padding: "6px 12px",
      cursor: "pointer",
      gap: "6px",
      width: "fit-content",
      color: "#1c2c4c",
      fontWeight: 500,
      fontSize: "14px",
    }}
  >
    <FaCalendarAlt style={{ fontSize: "14px" }} />
    <span>{value || "Select Date"}</span>
    <IoMdArrowDropdown style={{ fontSize: "16px", marginLeft: "4px" }} />
  </div>
));

const CancelAllModal = ({ open, onClose, doctorId, functionsOnSuccess }) => {
  const [cancelType, setCancelType] = useState("day");
  const [dateForCancellation, setDateForCancellation] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reason, setReason] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);
  const [customReason, setCustomReason] = useState("");
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") || ""
  );

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  // Fetch reason options
  const fetchSlotsData = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_BACKEND_ROUTE_URL}doctor_slot_cancel_reasons/`,

        { headers }
      );
      setReasonOptions(response?.data?.data); // Set the response data
    } catch (error) {
      console.error("Error fetching slots data:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSlotsData(); // Fetch data when the modal opens
    }
  }, [open]); // Trigger when modal opens or date changes

  const handleConfirm = async () => {
    if (!reason) {
      toast.error("Please select a remark before proceeding.");
      return;
    }

    if ((reason === "Other" || reason === "Others") && !customReason.trim()) {
      toast.error("Please enter a reason for 'Other'.");
      return;
    }

    const finalReason =
      reason === "Other" || reason === "Others" ? customReason.trim() : reason;

    const payload =
      cancelType === "day"
        ? {
            appointment_date: dateForCancellation?.toLocaleDateString("en-CA"),
            remarks: finalReason,
          }
        : {
            appointment_from_date: startDate?.toLocaleDateString("en-CA"),
            appointment_to_date: endDate?.toLocaleDateString("en-CA"),
            remarks: finalReason,
          };

    console.log(payload);

    try {
      const response = await axios.delete(
        `${REACT_APP_BACKEND_ROUTE_URL}cancel_doctor_slot/${doctorId}`,
        // payload,
        // { headers }
        {
          headers,
          data: payload,
        }
      );
      console.log("Response:", response.data);
      if (response?.data?.status === "Success") {
        toast.success(
          response?.data?.message || "Appointments deleted successfully!"
        );
        setCancelType("day");
        setCustomReason("");
        functionsOnSuccess();
        onClose();
      } else {
        toast.error(
          response?.data?.message ||
            "Some error occured while deleting. Please try again later."
        );
      }
    } catch (error) {
      console.error("Error during cancellation:", error);
      toast.error("An error occurred during cancellation. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "100%",
          maxWidth: 650,
          boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontWeight: 500,
            fontSize: "1.1rem",
          }}
        >
          Cancel All Appointments
        </Typography>
        <IconButton
          onClick={() => {
            setCancelType("single");
            onClose();
          }}
        >
          <CloseIcon sx={{ color: "#fff" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container>
          <Grid item sm={12} md={6}>
            <Typography
              sx={{ mb: 0.5, mt: 1, fontWeight: 500, color: "#0e204d" }}
            >
              Select Cancellation Type:
            </Typography>

            <RadioGroup
              value={cancelType}
              onChange={(e) => setCancelType(e.target.value)}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value="day"
                control={<Radio />}
                label="Cancel for a Day"
              />
              <FormControlLabel
                value="range"
                control={<Radio />}
                label="Cancel for Date Range"
              />
            </RadioGroup>
          </Grid>
          <Grid item sm={12} md={6}>
            {" "}
            {cancelType === "day" ? (
              <Box display="flex" sx={{ gap: 1, mt: 1 }}>
                <Typography
                  fontWeight={500}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    color: "#0e204d",
                  }}
                >
                  Select Date:{" "}
                </Typography>
                <DatePicker
                  selected={dateForCancellation}
                  onChange={(date) => setDateForCancellation(date)}
                  dateFormat="dd MMM yyyy"
                  customInput={<CustomDateInput />}
                  popperPlacement="bottom-start"
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                <Typography fontWeight={500} sx={{ color: "#0e204d" }}>
                  Select Date Range:
                </Typography>

                <Box alignItems="center" gap={2}>
                  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography
                      fontSize="14px"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        minWidth: 68,
                      }}
                    >
                      Start Date:
                    </Typography>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                        setEndDate(date);
                      }}
                      dateFormat="dd MMM yyyy"
                      selectsStart
                      //   startDate={startDate}
                      //   endDate={endDate}
                      customInput={<CustomDateInput />}
                      popperPlacement="bottom-start"
                    />
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography
                      fontSize="14px"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        minWidth: 68,
                      }}
                    >
                      End Date:
                    </Typography>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      dateFormat="dd MMM yyyy"
                      selectsEnd
                      //   startDate={startDate}
                      //   endDate={endDate}
                      minDate={startDate}
                      customInput={<CustomDateInput />}
                      popperPlacement="bottom-start"
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        <FormControl fullWidth>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1, mt: 1, fontWeight: 500 }}
          >
            Select Reason:
          </Typography>
          <TextField
            select
            size="small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            label="Reason"
          >
            {reasonOptions.map((option, idx) => (
              <MenuItem key={idx} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        {(reason === "Other" || reason === "Others") && (
          <Box sx={{ mt: 2 }}>
            <TextField
              size="small"
              label="Please specify reason"
              value={customReason}
              onChange={(e) =>
                setCustomReason(e.target.value.replace(/  +/g, " "))
              }
              required
              fullWidth
              inputProps={{ maxLength: 30 }}
            />
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "right",
                color: "text.secondary",
                mt: 0.5,
                fontSize: "0.75rem",
              }}
            >
              {customReason.length}/30
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#121959",
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#121959",
              },
            }}
            onClick={() => {
              onClose();
            }}
            size="small"
          >
            Close
          </Button>

          <Button
            variant="contained"
            sx={{
              borderRadius: "10px",
              backgroundColor: "#121959",
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              "&:hover": {
                backgroundColor: "#121959",
              },
            }}
            onClick={handleConfirm}
            size="small"
          >
            Confirm Cancellation
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CancelAllModal;
